/**
 * Unified Analytics Interface
 *
 * Single interface for all analytics tracking (PostHog + Datadog)
 * This is the ONLY file the app should import for analytics
 *
 * IMPORTANT: Never import posthog.ts or datadog.ts directly in your app code!
 *
 * @example
 * ```typescript
 * import { analytics, useAnalyticsAutoCapture, DatadogProvider } from '@/utils/analytics';
 *
 * // In root layout - wrap app with DatadogProvider
 * <DatadogProvider>
 *   <YourApp />
 * </DatadogProvider>
 *
 * // Track events
 * trackEvent('button_clicked', { button: 'submit' });
 * identifyUser(user);
 * ```
 */
import type { User } from '@/types/user';

import * as Datadog from '../libs/datadog';
import * as PostHog from '../libs/posthog';

export type AppOpenSource = 'fresh_open' | 'foreground';

/**
 * Initialize user (login)
 */
const identifyUser = async (user: User): Promise<void> => {
  try {
    PostHog.initializeUser(user);
    await Datadog.initializeUser(user);
  } catch (error) {
    console.error('Analytics: Failed to identify user', error);
  }
};

/**
 * Reset user (logout)
 */
const resetUser = async (): Promise<void> => {
  try {
    PostHog.resetUser();
    await Datadog.resetUser();
  } catch (error) {
    console.error('Analytics: Failed to reset user', error);
  }
};

/**
 * Track custom event
 */
const trackEvent = (eventName: string, properties?: Record<string, any>): void => {
  try {
    PostHog.trackEvent(eventName, properties);
    // Datadog doesn't track custom events the same way
    // Actions are logged via RUM automatically
  } catch (error) {
    console.error('Analytics: Failed to track event', error);
  }
};

/**
 * Track screen view
 */
const trackScreen = async (screenName: string, properties?: Record<string, any>): Promise<void> => {
  try {
    PostHog.trackScreen(screenName, properties);
    await Datadog.trackView(screenName, screenName, properties);
  } catch (error) {
    console.error('Analytics: Failed to track screen', error);
  }
};

/**
 * Track error
 */
const trackError = async (error: Error | string, context?: Record<string, any>): Promise<void> => {
  try {
    const errorMessage = error instanceof Error ? error.message : error;

    PostHog.trackEvent('error', { error: errorMessage, ...context });
    await Datadog.trackError(error, context);
  } catch (err) {
    console.error('Analytics: Failed to track error', err);
  }
};

/**
 * Log an operational error to Datadog Logs (logger.error) — the migration
 * equivalent of Sparta's `logErrorMessage`. Distinct from `trackError`, which
 * records a RUM error plus a PostHog `error` event. Use this for errors you
 * monitor / alert on via Datadog Logs.
 */
const logError = async (message: string, context?: Record<string, any>): Promise<void> => {
  try {
    // Mirror Sparta's non-prod console.error.
    if (__DEV__) {
      console.error(message, context);
    }
    await Datadog.logError(message, context);
  } catch (err) {
    console.error('Analytics: Failed to log error', err);
  }
};

/**
 * Log an expected, non-fault event to Datadog Logs (logger.info) — the
 * migration equivalent of Sparta's `Datadog.sendInfo`. Use for outcomes worth
 * recording but that aren't errors (e.g. an existing-account collision), so
 * they don't inflate error-level alerts.
 */
const logInfo = async (message: string, context?: Record<string, any>): Promise<void> => {
  try {
    if (__DEV__) {
      console.info(message, context);
    }
    await Datadog.logInfo(message, context);
  } catch (err) {
    console.error('Analytics: Failed to log info', err);
  }
};

/**
 * Set user properties
 */
const setUserProperties = async (properties: Record<string, any>): Promise<void> => {
  try {
    PostHog.setUserProperties(properties);
    await Datadog.setUserProperties(properties);
  } catch (error) {
    console.error('Analytics: Failed to set user properties', error);
  }
};

/**
 * Set Datadog-only global-context properties (mirrors Sparta's
 * `Datadog.setGlobalContext`). Use for context that belongs on Datadog RUM/Logs
 * but not as a PostHog person property — e.g. `programRecommendationType`. On
 * web these become persistent global-context props; on native they're set as
 * global RUM attributes (DdSdkReactNative.addAttribute).
 */
const setDatadogGlobalContext = async (properties: Record<string, any>): Promise<void> => {
  try {
    await Datadog.setUserProperties(properties);
  } catch (error) {
    console.error('Analytics: Failed to set Datadog global context', error);
  }
};

/**
 * Remove user property
 */
const removeUserProperty = async (key: string): Promise<void> => {
  try {
    PostHog.removeUserProperty(key);
    await Datadog.removeUserProperty(key);
  } catch (error) {
    console.error('Analytics: Failed to remove user property', error);
  }
};

/**
 * Get feature flag value (PostHog only)
 */
const getFeatureFlag = async (key: string): Promise<string | boolean | undefined> => {
  try {
    return await PostHog.getFeatureFlag(key);
  } catch (error) {
    console.error('Analytics: Failed to get feature flag', error);
    return undefined;
  }
};

/**
 * Subscribe to PostHog feature-flag (re)loads; returns an unsubscribe fn. The
 * flag hooks use this to refresh after a post-identify reload, so a read that
 * ran before the user was identified updates for the current user.
 */
const onFeatureFlags = (callback: () => void): (() => void) => {
  try {
    return PostHog.onFeatureFlags(callback);
  } catch (error) {
    console.error('Analytics: Failed to subscribe to feature flags', error);
    return () => {};
  }
};

/**
 * Check if feature flag is enabled (PostHog only)
 */
const isFeatureFlagEnabled = async (key: string): Promise<boolean> => {
  try {
    return await PostHog.isFeatureFlagEnabled(key);
  } catch (error) {
    console.error('Analytics: Failed to check feature flag', error);
    return false;
  }
};

/**
 * Track app open event (web only via Snowplow)
 */
const trackAppOpen = (_source: AppOpenSource = 'fresh_open'): void => {
  try {
  } catch (error) {
    console.error('Analytics: Failed to track app_open', error);
  }
};

// Named exports for convenience
export {
  identifyUser,
  resetUser,
  trackEvent,
  trackScreen,
  trackError,
  logError,
  logInfo,
  trackAppOpen,
  setUserProperties,
  setDatadogGlobalContext,
  removeUserProperty,
  getFeatureFlag,
  onFeatureFlags,
  isFeatureFlagEnabled,
};

/**
 * Aggregate analytics object — mirrors VidaReactNative's `analytics` export
 * (`@/utils/analytics`) so cross-repo-synced modules (e.g.
 * `useAddressVerification`) can call `analytics.trackEvent(...)` /
 * `analytics.trackError(...)` with an identical body in both repos. See SYNC.md.
 */
export const analytics = {
  identifyUser,
  resetUser,
  trackEvent,
  trackScreen,
  trackError,
  logError,
  logInfo,
  trackAppOpen,
  setUserProperties,
  setDatadogGlobalContext,
  removeUserProperty,
  getFeatureFlag,
  isFeatureFlagEnabled,
};
